import { useQueryClient } from '@tanstack/react-query';

import type { ContactAddressDTO } from '@invenco/common-interface/shared';
import type { AsnDTO, AsnLineDTO } from '@invenco/supply-interface';

import { useGateways } from '@/gateways/GatewayProvider';
import { removeFalsyKeys } from '@/shared/helpers/object';
import { useEntityDetailsQuery, useGatewayMutation } from '@/shared/hooks/queries';

import type { AsnLineAddInput, AsnLineUpdatePatch } from '../asn-lines-card/asn-lines-card-columns';

const getAsnUpdateDTO = (asn: Partial<AsnDTO>) => ({
  ...asn,
  fromAddress: asn.fromAddress
    ? (removeFalsyKeys(asn.fromAddress) as ContactAddressDTO)
    : undefined,
});

const getRecostPatch = (lineId: string, patch: AsnLineUpdatePatch) => ({
  id: lineId,
  ...(patch.unitPurchaseCost !== undefined ? { unitPurchaseCost: patch.unitPurchaseCost } : {}),
  ...(patch.unitCostTotal !== undefined ? { unitCostTotal: patch.unitCostTotal } : {}),
});

export function useAsn(id: string) {
  const { supplyGateway } = useGateways();
  const queryClient = useQueryClient();
  const asnQueryKey = ['asns', id];

  const {
    data: asn,
    isLoading,
    refetch: refetchAsn,
  } = useEntityDetailsQuery<AsnDTO>({
    parentKey: 'asns',
    id,
    query: (fetchId, { signal }) => supplyGateway.getAsn(fetchId, { signal }),
  });

  const updateCachedAsn = (updater: (currentAsn: AsnDTO) => AsnDTO) => {
    queryClient.setQueryData<AsnDTO>(asnQueryKey, (currentAsn) =>
      currentAsn ? updater(currentAsn) : currentAsn,
    );
  };

  const setCachedAsn = (cachedAsn: AsnDTO | undefined) => {
    if (cachedAsn) {
      queryClient.setQueryData(asnQueryKey, cachedAsn);
    }
  };

  const { mutate: addAsnLine } = useGatewayMutation<AsnLineDTO, AsnLineAddInput>({
    mutationFn: (line) => supplyGateway.addAsnLine(id, line),
    onSuccess: (newLine) => {
      updateCachedAsn((currentAsn) => ({
        ...currentAsn,
        asnLines: [...(currentAsn.asnLines ?? []), newLine],
      }));
    },
    linkedQuery: asnQueryKey,
    linkResponseToQuery: false,
    successMessage: 'Item added',
  });

  const { mutate: updateAsnLineMutation } = useGatewayMutation<
    AsnLineDTO,
    { lineId: string; patch: AsnLineUpdatePatch }
  >({
    mutationFn: ({ lineId, patch }) => supplyGateway.updateAsnLine(id, lineId, patch),
    onSuccess: (updatedLine) => {
      updateCachedAsn((currentAsn) => ({
        ...currentAsn,
        asnLines: (currentAsn.asnLines ?? []).map((line) =>
          line.id === updatedLine.id ? updatedLine : line,
        ),
      }));
    },
    linkedQuery: asnQueryKey,
    linkResponseToQuery: false,
    successMessage: 'Item updated',
  });

  const { mutate: recostAsnLineMutation } = useGatewayMutation<
    AsnDTO,
    { lineId: string; patch: AsnLineUpdatePatch }
  >({
    mutationFn: ({ lineId, patch }) =>
      supplyGateway.recostAsnLines(id, [getRecostPatch(lineId, patch)]),
    onSuccess: (updatedAsn) => queryClient.setQueryData(asnQueryKey, updatedAsn),
    linkedQuery: asnQueryKey,
    successMessage: 'Item recosted',
  });

  const { mutate: cancelAsn } = useGatewayMutation({
    mutationFn: () => supplyGateway.cancelAsn(id),
    linkedQuery: asnQueryKey,
    successMessage: 'ASN cancelled',
  });

  const { mutate: bookAsn } = useGatewayMutation({
    mutationFn: () => supplyGateway.bookAsn(id),
    linkedQuery: asnQueryKey,
    successMessage: 'ASN booked',
  });

  const { mutate: updateAsn } = useGatewayMutation<AsnDTO, Partial<AsnDTO>, AsnDTO | undefined>({
    mutationFn: (input) => supplyGateway.updateAsn(id, getAsnUpdateDTO(input)),
    linkedQuery: asnQueryKey,
    successMessage: 'ASN updated',
    onMutate: (input) => {
      const previousAsn = queryClient.getQueryData<AsnDTO>(asnQueryKey);

      if (previousAsn) {
        queryClient.setQueryData(asnQueryKey, { ...previousAsn, ...input });
      }

      return previousAsn;
    },
    onError: (_error, _input, previousAsn) => setCachedAsn(previousAsn),
  });

  const recostAsnLine = (lineId: string, patch: AsnLineUpdatePatch) =>
    recostAsnLineMutation({ lineId, patch });

  const updateAsnLine = (lineId: string, patch: AsnLineUpdatePatch) =>
    updateAsnLineMutation({ lineId, patch });

  return {
    asn,
    isLoading,
    refetchAsn,
    updateAsn,
    bookAsn,
    cancelAsn,
    addAsnLine,
    updateAsnLine,
    recostAsnLine,
  };
}
